"use client";

import Link from "next/link";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";

import { sortRomaReserveLocations, type RomaReserveLocation } from "./roma-types";

type RomaReserveSheetProps = {
  locations: readonly RomaReserveLocation[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function locationMetaLine(location: RomaReserveLocation): string {
  if (location.slug === "southern-highlands") {
    return "S Decatur Blvd · quiet dining room, from 4pm";
  }

  return "Horizon Ridge Pkwy · bar pours till 2am";
}

export function RomaReserveSheet({
  locations,
  open,
  onOpenChange,
}: RomaReserveSheetProps) {
  const sortedLocations = sortRomaReserveLocations(locations);

  if (sortedLocations.length === 0) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className={cn(
          "gap-0 rounded-t-2xl border-[var(--roma-parchment-line,#2a2a2a)] bg-[var(--background,#0d0d0d)] p-0 pb-[env(safe-area-inset-bottom)]",
          "data-[state=open]:duration-300 data-[state=closed]:duration-200",
        )}
        aria-describedby="roma-reserve-sheet-description"
      >
        <div className="px-5 pt-5 pb-3">
          <SheetTitle
            id="roma-reserve-sheet-title"
            className="font-serif text-2xl font-normal text-[var(--foreground,#ffffff)]"
          >
            Which Roma?
          </SheetTitle>
          <SheetDescription
            id="roma-reserve-sheet-description"
            className="mt-1.5 text-sm text-[var(--muted-foreground,#a3a3a3)]"
          >
            Choose a location to continue with Google Reserve.
          </SheetDescription>
        </div>

        <div className="space-y-2 px-5 pb-4">
          {sortedLocations.map((location) => (
            <SheetClose key={location.slug} asChild>
              <a
                href={location.reserveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-[4.5rem] flex-col justify-center rounded-xl border border-[var(--border,#2a2a2a)] bg-[var(--card,#1a1a1a)] px-4 py-3 transition-colors hover:border-[var(--roma-terracotta,#f45c24)]/40 hover:bg-[var(--card,#1e1e1e)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--roma-terracotta,#f45c24)]"
                /* The sheet renders in a portal outside [data-homepage], so the
                   delegated data-track listener never sees these clicks. Fire
                   the reservation conversion explicitly instead. */
                onClick={() =>
                  analytics.reservationClicked("roma_reserve_sheet", {
                    label: location.slug,
                    cta_text: "Reserve",
                    destination_url: location.reserveUrl,
                  })
                }
              >
                <span className="text-base font-semibold text-[var(--foreground,#ffffff)]">
                  {location.shortName}
                </span>
                <span className="mt-0.5 text-sm text-[var(--muted-foreground,#a3a3a3)]">
                  {locationMetaLine(location)}
                </span>
              </a>
            </SheetClose>
          ))}
        </div>

        <div className="border-t border-[var(--border,#2a2a2a)] px-5 py-3">
          <SheetClose asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-11 w-full text-[var(--muted-foreground,#a3a3a3)] hover:bg-[var(--accent,#1e1e1e)] hover:text-[var(--foreground,#ffffff)]"
            >
              Cancel
            </Button>
          </SheetClose>
          <p className="mt-2 text-center text-xs text-[var(--muted-foreground,#a3a3a3)]">
            <Link
              href="/reservations#picker"
              className="underline-offset-2 hover:text-[var(--roma-terracotta,#f45c24)] hover:underline"
              onClick={() => {
                analytics.reservationClicked("roma_reserve_sheet", {
                  label: "all-locations",
                  cta_text: "See both locations on the reservations page",
                  destination_url: "/reservations#picker",
                });
                onOpenChange(false);
              }}
            >
              See both locations on the reservations page
            </Link>
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
